/* eslint-disable no-nested-ternary */
import {
  BlueBirdTheme, Checkbox, Typography,
} from "@kitbird/core";
import {
  Control, Controller, UseFormSetValue,
} from "react-hook-form";
import {
  CardBody, CardContainer, CardHeader, CardSelectAll, CardTitle,
} from "../BusinessCard";
import {
  LayoutContainer, SelectionGroupBody, SelectionGroupContainer, SelectionGroupHeader,
} from "../../BusinessTrx.styled";
import { IBusinessTrxFormValues, CompanyPoolData, CompanyPools } from "../../types";
import BaseLoading from "../../../../../components/Loading/BaseLoading";
import EmptyState from "../EmptyState";
import { useFormBusinessTrx } from "../../FormBusinessTrxProvider";

interface StepCompanySelectionProps {
  control: Control<IBusinessTrxFormValues>;
  setValue: UseFormSetValue<IBusinessTrxFormValues>;
}

const StepCompanySelection = ({ control, setValue }: StepCompanySelectionProps) => {
  const {
    companies,
    allCompanyIds,
    allCompanyRelations,
    isFetchingCompany,
    resCompanyByPool,
    selectedCompanyIds,
    selectedCompanyRelations,
    isEditMode,
  } = useFormBusinessTrx();

  return (
    <CardContainer>
      <CardHeader>
        <CardTitle>
          <Typography level={4} fontWeight="bold" color={BlueBirdTheme.colors.gray.subtitle}>
            Pilih perusahaan
          </Typography>
        </CardTitle>
        {!isEditMode && (
          <CardSelectAll>
            <Controller
              name="pilihSemuaPerusahaan"
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <Checkbox
                  {...restField}
                  checked={value}
                  onChange={(e) => {
                    const { checked } = e.target;
                    onChange(checked);
                    setValue("selectedCompanyIds", checked ? allCompanyIds : []);
                    setValue("selectedCompanyRelations", checked ? allCompanyRelations : []);
                  }}
                  label="Pilih semua perusahaan"
                  disabled={isFetchingCompany || allCompanyIds.length === 0}
                />
              )}
            />
          </CardSelectAll>
        )}
      </CardHeader>
      <CardBody layout="flex" flexDirection="column">
        {isFetchingCompany ? (
          <BaseLoading />
        ) : resCompanyByPool.length ? (
          Object.entries(companies).map((
            [areaName, companyPools]: [string, CompanyPools],
          ) => (
            <div key={areaName}>
              <Typography level={4} fontWeight="bold" color={BlueBirdTheme.colors.gray.subtitle}>
                Area {areaName}
              </Typography>
              <LayoutContainer layout="grid" columns="repeat(2, 1fr)" marginTop="12px">
                {Object.entries(companyPools).map(([poolName, poolData]:
                  [string, CompanyPoolData]) => {
                  const allCompanyIdsInPool = poolData.companies.map((c) => `${poolData.pool_id}.${c.company_code}`);
                  const allCompanyRelationsInPool = poolData.companies.map((c) => `${poolData.pool_id}.${c.company_relation}`);
                  return (
                    <SelectionGroupContainer key={poolName}>
                      <SelectionGroupHeader>
                        <Typography level={4} fontWeight="bold" color={BlueBirdTheme.colors.gray.subtitle}>
                          {`Pilih perusahaan untuk pool ${poolData.pool_id} - ${poolName}`}
                        </Typography>
                      </SelectionGroupHeader>
                      <SelectionGroupBody gridTemplateRows="none">
                        {!isEditMode && (
                          <Checkbox
                            label="Pilih semua"
                            checked={allCompanyIdsInPool.length > 0
                              && allCompanyIdsInPool
                                .every((id) => selectedCompanyIds.includes(id))}
                            onChange={(e) => {
                              const currentSelectedIds = new Set(selectedCompanyIds);
                              const currentSelectedRelations = new Set(
                                selectedCompanyRelations,
                              );
                              if (e.target.checked) {
                                allCompanyIdsInPool.forEach(
                                  (id) => currentSelectedIds.add(id),
                                );
                                allCompanyRelationsInPool.forEach(
                                  (relation) => currentSelectedRelations.add(relation),
                                );
                              } else {
                                allCompanyIdsInPool.forEach(
                                  (id) => currentSelectedIds.delete(id),
                                );
                                allCompanyRelationsInPool.forEach(
                                  (relation) => currentSelectedRelations.delete(relation),
                                );
                              }
                              setValue("selectedCompanyIds", Array.from(currentSelectedIds));
                              setValue("selectedCompanyRelations", Array.from(currentSelectedRelations));
                            }}
                          />
                        )}
                        {poolData.companies.map((company: {
                          company_code: string,
                          company_relation: string
                        }) => {
                          const uniqueValue = `${poolData.pool_id}.${company.company_code}`;
                          const uniqueRelationValue = `${poolData.pool_id}.${company.company_relation}`;
                          return (
                            <Controller
                              key={`${areaName}-${poolName}-${company.company_code}`}
                              name="selectedCompanyIds"
                              control={control}
                              defaultValue={[]}
                              render={({ field: { value, onChange } }) => (
                                <Checkbox
                                  label={company.company_code}
                                  checked={value.includes(uniqueValue)}
                                  onChange={(e) => {
                                    if (isEditMode) {
                                      onChange(e.target.checked ? [uniqueValue] : []);
                                      setValue("selectedCompanyRelations", e.target.checked ? [uniqueRelationValue] : []);
                                    } else {
                                      const newSelectedIds = new Set(value);
                                      const newSelectedRelations = new Set(
                                        selectedCompanyRelations,
                                      );
                                      if (e.target.checked) {
                                        newSelectedIds.add(uniqueValue);
                                        newSelectedRelations.add(uniqueRelationValue);
                                      } else {
                                        newSelectedIds.delete(uniqueValue);
                                        newSelectedRelations.delete(uniqueRelationValue);
                                      }
                                      onChange(Array.from(newSelectedIds));
                                      setValue("selectedCompanyRelations", Array.from(newSelectedRelations));
                                    }
                                  }}
                                />
                              )}
                            />
                          );
                        })}
                      </SelectionGroupBody>
                    </SelectionGroupContainer>
                  );
                })}
              </LayoutContainer>
            </div>
          ))
        ) : (
          <EmptyState />
        )}
      </CardBody>
    </CardContainer>
  );
};

export default StepCompanySelection;
